import { collection } from 'infra/collection'
import { Id, PrimeProps } from 'types/definitions'
import { ProviderAPI } from './API'
import { ProviderDTO, UpdateProviderUserDataPayload } from './dtos/provider'
import Provider from './models/Provider'

export type PrimeProvider = (
  props: PrimeProps<ProviderDTO, 'user_public_id'>,
) => Provider | undefined

export default class ProviderStore {
  /**
   * Provider collection.
   */
  providers = collection<ProviderDTO, Provider>({
    idAttribute: 'user_public_id',
    create: () => new Provider(),
  })

  constructor(private providerAPI: ProviderAPI) {}

  /**
   * Fetches provider data if it is not already in the store.
   * Returns immediately.
   * @param props
   */
  primeProvider: PrimeProvider = (props) => {
    if (!props?.user_public_id) {
      return
    }

    const provider = this.providers.set({
      user_public_id: props.user_public_id,
    })

    this.fetchByPublicId(props.user_public_id)

    return provider
  }

  /**
   * Fetches provider data for a user.
   * @param publicId
   */
  async fetchByPublicId(publicId: Id): Promise<void> {
    const provider = await this.providerAPI.get(Id.assert(publicId))

    this.providers.set(provider)
  }

  /**
   * Updates a provider's editable user data, then refreshes the cached
   * Provider so observers re-render with the persisted values.
   * @param publicId
   * @param payload
   */
  async update(
    publicId: Id,
    payload: UpdateProviderUserDataPayload,
  ): Promise<void> {
    await this.providerAPI.update(Id.assert(publicId), payload)
    await this.fetchByPublicId(publicId)
  }
}
